import classNames from 'classnames';
import { forwardRef } from 'react';
import { useFieldState, WithFieldStateProps } from '../../hooks';
export interface SelectProps
  extends Omit<React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>, 'size'>,
    WithFieldStateProps {
  size?: 'medium' | 'small';
}

const SelectIcon = `
  <svg viewBox='-2 -8 22 22' fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.47575 0.75H10.5215C11.1472 0.75 11.4601 1.50586 11.0172 1.94883L6.49607 6.47344C6.22185 6.74766 5.77536 6.74766 5.50115 6.47344L0.980052 1.94883C0.537083 1.50586 0.849974 0.75 1.47575 0.75Z" fill="white"/>
  </svg>`;

/**
 * Prestyled input field with extended field states
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const { className, children, disabled, submitting, error, dirty, readOnly, size = 'medium', ...otherProps } = props;

  const fieldState = useFieldState(props);
  const encodedSvg = encodeURIComponent(SelectIcon);
  return (
    <select
      {...otherProps}
      ref={ref}
      disabled={disabled || submitting || readOnly}
      className={classNames(
        '[-moz-padding-start:0.5rem]', // fix strange firefox offset for select on the left side (the offset seems add 0.25rem to the left)
        size === 'medium'
          ? classNames(
              'bg-[right_0.5rem_center] sm:text-sm',
              !readOnly ? 'pl-3 pr-10 py-2' : 'px-3 py-2', // padding for the bg icon
            )
          : undefined,
        size === 'small'
          ? classNames(
              'bg-[right_0.25rem_center] text-xs leading-[normal]',
              !readOnly ? 'pl-2 pr-8 py-1' : 'px-2 py-1', // padding for the bg icon
            )
          : undefined,
        'bg-no-repeat bg-[length:1.5em_1.5em]', // bg icon
        'appearance-none text-white bg-surface-elevated dark:bg-neutral-700 focus:outline-none focus:ring-offset-0 focus:ring-1', // prestyling
        'block w-full rounded-md shadow-sm',
        fieldState === 'default' &&
          'border-gray-300 focus:ring-brand-primary focus:border-brand-primary dark:border-neutral-800 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
        fieldState === 'dirty' &&
          'border-brand-primary focus:ring-brand-primary focus:border-brand-primary dark:border-sky-300 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
        fieldState === 'error' &&
          'border-red-500 focus:ring-red-500 focus:border-red-500 placeholder-red-300 text-red-800 dark:border-red-400 focus:dark:ring-red-400 focus:dark:border-red-400',
        fieldState === 'readOnly' &&
          'disabled:text-inherit disabled:opacity-100 disabled:bg-disabled disabled:border-neutral-50 disabled:placeholder-inherit disabled:focus:ring-0 disabled:cursor-default disabled:dark:bg-neutral-900 disabled:dark:border-neutral-900',
        fieldState === 'disabled' &&
          'disabled:opacity-100 disabled:shadow-none disabled:border-gray-300 disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-neutral-400 disabled:cursor-not-allowed disabled:dark:border-neutral-800',
        fieldState === 'disabledError' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-red-300 disabled:focus:ring-0 disabled:placeholder-red-200 disabled:text-red-400 disabled:cursor-not-allowed disabled:dark:border-red-200',
        fieldState === 'disabledDirty' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-sky-300 disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:cursor-not-allowed disabled:dark:border-sky-200',
        fieldState === 'submitting' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-gray-300 disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-neutral-800',
        fieldState === 'submittingDirty' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-sky-300 disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-sky-200',
        className,
      )}
      style={{
        backgroundImage: !readOnly ? `url("data:image/svg+xml;utf8,${encodedSvg}")` : undefined,
        ...otherProps.style,
      }}
    >
      {children}
    </select>
  );
});

export default Select;
